// Interface

import util from '@/utils/classToObject'
import { IEnseigneState } from './state'
import { AloDbEnseigne, IEnseigneConfig } from '@/models/alodb/AloDbEnseigne'
import { FleetType } from '@/enum/FleetType'
import { MissionGlobalType } from '@/enum/MissionGlobalType'
import { allowedFleetTypes, allowedMissionGlobalTypes, isFleetTypeAllowed, isMissionGlobalTypeAllowed } from '@/utils/tenantModules'

export class EnseigneGetter {  
      
  public enseignes(state:IEnseigneState,getters:any) {
    return state.enseignes
  }

  public enseignesById(state:IEnseigneState) {
    return state.enseignes.reduce(
      (acc, enseigne) => {
        acc[enseigne.ENS_ID!] = enseigne
        return acc
      },
      {} as { [id: number]: AloDbEnseigne }
    )
  }  

  public modifiedAdmEnseigne(state:IEnseigneState,getters:any) {
    return state.modifiedAdmEnseigne
  }

  public originalAdmEnseigne(state:IEnseigneState,getters:any) {
    return state.originalAdmEnseigne
  }

  public enseigneLoading(state:IEnseigneState,getters:any) {
    return state.enseigneLoading
  }

  public enseigneStatus(state:IEnseigneState) {     
    return state.status
  }

  public adminEnseigneFilter(state:IEnseigneState) {     
    return state.adminFilter
  }

  // ── Modules par enseigne (BLF037, RG-ACC-047..053) ─────────────────────────────────────────
  // Les réglages qui s'appliquent à une flotte ou à un scénario sont ceux de SON enseigne
  // (modèle : applyAisSecurityOverride) : enseigne de l'utilisateur → liste des enseignes si elle
  // y est, sinon userProfile.config ; autre enseigne → liste des enseignes ; inconnue → refus.

  // Rôles qui voient les objets d'autres enseignes (V_ROLE_FLEET) : la liste des enseignes leur est chargée.
  public isMultiTenantRole(state: IEnseigneState, getters: any): boolean {
    return !!(getters.isSysAdmin || getters.isOperator || getters.isManager)
  }

  public enseigneConfigFor(state: IEnseigneState, getters: any) {
    return (ensId: number | null | undefined): IEnseigneConfig | null => {
      const user = getters.userProfile
      if (ensId == null || !user) return null
      const known = getters.enseignesById[ensId] as AloDbEnseigne | undefined
      if (known) return known.config
      return ensId === user.ensId ? (user.config as IEnseigneConfig) : null
    }
  }

  public isFleetAllowed(state: IEnseigneState, getters: any) {
    return (fleet: { ENS_ID?: number | null; FLE_TYP?: number | null } | null | undefined): boolean => {
      if (!fleet) return false
      const config = getters.enseigneConfigFor(fleet.ENS_ID)
      return !!config && isFleetTypeAllowed(config, fleet.FLE_TYP)
    }
  }

  public isMissionGlobalAllowed(state: IEnseigneState, getters: any) {
    return (mig: { ENS_ID?: number | null; MIG_TYP?: number | null } | null | undefined): boolean => {
      if (!mig) return false
      const config = getters.enseigneConfigFor(mig.ENS_ID)
      return !!config && isMissionGlobalTypeAllowed(config, mig.MIG_TYP)
    }
  }

  // Types proposés à la création dans l'enseigne ensId ; inconnue → aucun.
  public fleetTypesFor(state: IEnseigneState, getters: any) {
    return (ensId: number | null | undefined): FleetType[] => {
      const config = getters.enseigneConfigFor(ensId)
      return config ? allowedFleetTypes(config) : []
    }
  }

  public missionGlobalTypesFor(state: IEnseigneState, getters: any) {
    return (ensId: number | null | undefined): MissionGlobalType[] => {
      const config = getters.enseigneConfigFor(ensId)
      return config ? allowedMissionGlobalTypes(config) : []
    }
  }

  // Réglages des enseignes vues : la sienne, plus la liste pour un rôle multi-enseignes.
  public visibleEnseigneConfigs(state: IEnseigneState, getters: any): IEnseigneConfig[] {
    const own = getters.enseigneConfigFor(getters.userProfile?.ensId)
    const list = own ? [own] : []
    if (getters.isMultiTenantRole) list.push(...state.enseignes.map(e => e.config))
    return list
  }

  // Enseigne cible d'une création (FleetNew, SimulationNew) : filtre d'en-tête, sinon la sienne.
  public creationEnsId(state: IEnseigneState, getters: any): number | null {
    return getters.filterMission?.ensIds?.at(0) ?? getters.userProfile?.ensId ?? null
  }

  // Types proposés à la création dans l'enseigne cible (RG-ACC-052) ; aucun → bouton masqué.
  public creationFleetTypes(state: IEnseigneState, getters: any): FleetType[] {
    return getters.fleetTypesFor(getters.creationEnsId)
  }

  public creationMissionGlobalTypes(state: IEnseigneState, getters: any): MissionGlobalType[] {
    return getters.missionGlobalTypesFor(getters.creationEnsId)
  }

  // Tuile et routes d'un module (RG-ACC-051).
  public hasFleetModule(state: IEnseigneState, getters: any): boolean {
    return getters.visibleEnseigneConfigs.some((c: IEnseigneConfig) => c.fleet_on)
  }

  public hasSmartPlanningModule(state: IEnseigneState, getters: any): boolean {
    return getters.visibleEnseigneConfigs.some((c: IEnseigneConfig) => c.smartplanning_on)
  }

  // Types autorisés dans au moins une enseigne vue (filtres de type des listes).
  public knownMissionGlobalTypes(state: IEnseigneState, getters: any): MissionGlobalType[] {
    const allowed = new Set(getters.visibleEnseigneConfigs.flatMap((c: IEnseigneConfig) => allowedMissionGlobalTypes(c)))
    return [MissionGlobalType.SIMULATION, MissionGlobalType.COVERAGE, MissionGlobalType.FORMATION].filter(t => allowed.has(t))
  }
  
}

export default util.classToObject(EnseigneGetter)